import { lessonsFor, mc, tf } from "./helpers";
import type { LessonContentMeta } from "./types";

const guide = { label: "AI Safety Guide", href: "/security-guide" };
const checklist = { label: "Security Checklist", href: "/security-checklist" };
const chat = { label: "Chat with GenieBot", href: "/chat" };

const REVIEWED = "2026-10-12";

const nist: LessonContentMeta = {
  contentVersion: "1.0",
  author: "GenieBot Learning",
  lastReviewedAt: REVIEWED,
  nextReviewAt: "2027-04-12",
  freshness: "review-semiannually",
  sources: [
    { title: "AI Risk Management Framework", organization: "NIST", url: "https://www.nist.gov/itl/ai-risk-management-framework" },
    { title: "AI Competency Framework", organization: "UNESCO", url: "https://www.unesco.org/en/articles/ai-competency-framework-for-students" },
  ],
};

/**
 * AI Safety expansion (items from the learning audit). Existing lessons are
 * untouched; these are appended to the track in learningHub.ts.
 */
export const AI_SAFETY_EXPANDED = lessonsFor("ai-safety", [
  {
    slug: "bias-and-fairness",
    title: "Bias and Fairness in AI",
    minutes: 5,
    learn: ["Why AI can reflect patterns and bias in its training data", "How to review output for unfair assumptions"],
    explanation: [
      "AI learns from data created by people and institutions, so it can repeat — and sometimes amplify — the patterns and biases in that data. Output is not automatically neutral.",
      "Different groups can be affected differently by the same output. When AI touches hiring, lending, health, education or public communication, review it for assumptions about people before you use it.",
    ],
    example: "An assistant drafts interview questions that assume the candidate is a recent graduate. A reviewer rewrites them to focus on skills, not age or background.",
    interactive: "spot-the-assumption",
    check: mc("single", "Why can AI output be unfair even when it sounds polished?", ["It is programmed to be unfair", "*It learns patterns from data that can contain bias", "It cannot be unfair because it is software"], "AI reflects its training data. Polished wording says nothing about fairness."),
    takeaway: "AI output is not automatically neutral — review it for assumptions about people.",
    tool: chat,
    resource: guide,
    meta: nist,
  },
  {
    slug: "automation-bias",
    title: "Automation Bias and Over-Reliance",
    minutes: 5,
    learn: ["Why confident automation earns unearned trust", "Keeping independent judgment on high-stakes work"],
    explanation: [
      "People tend to trust confident, fast automation — and to stop checking once a system seems reliable. That is automation bias, and confidence is not proof of correctness.",
      "The fix is a habit, not a setting: on anything high-stakes, form your own view first or verify independently before accepting the machine's answer.",
    ],
    example: "A navigation app routes drivers onto a closed road. Drivers who glanced at the road signs caught it; those who followed blindly got stuck.",
    interactive: "double-check-this",
    check: mc("scenario", "An AI tool has been right ten times in a row. What should you do on the eleventh, high-stakes answer?", ["Trust it — it has earned it", "*Verify it the same way you verified the first one", "Stop using the tool entirely"], "A good track record lowers, but never removes, the need to check important answers."),
    takeaway: "Confidence is not proof. High-stakes output always gets independent judgment.",
    tool: chat,
    resource: guide,
    meta: nist,
  },
  {
    slug: "copyright-and-attribution",
    title: "Copyright, Attribution and Intellectual Property",
    minutes: 5,
    learn: ["Why AI output can still need review for IP issues", "That rules differ by organization, platform and country"],
    explanation: [
      "AI-generated text, images and code can raise copyright and attribution questions, and the rules are still evolving. Schools, employers and platforms each set their own disclosure requirements.",
      "Two safe habits: never present generated material as verified original research, and check the rules that apply where you publish. This lesson is educational — for legal questions, talk to a qualified professional.",
    ],
    example: "A marketer checks their company's AI policy before publishing AI-drafted copy, and adds the disclosure the policy requires.",
    check: tf("If AI generated it, nobody needs to check copyright or attribution rules.", false, "You remain responsible for what you publish, and disclosure rules vary by organization and platform."),
    takeaway: "You own what you publish — know the rules that apply before you share AI-assisted work.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
    meta: nist,
  },
  {
    slug: "synthetic-media",
    title: "Synthetic Media and Disclosure",
    minutes: 6,
    difficulty: "Intermediate",
    learn: ["Recognize AI-generated images, video and voice", "Verify before trusting — and disclose when you create"],
    explanation: [
      "AI can now generate realistic images, video and cloned voices, including deepfakes. No one — including you — can reliably identify every fake by appearance alone, so verification beats inspection.",
      "Verify surprising media through the source: check who published it, look for the same claim from independent reputable outlets, and be suspicious of anything urgent asking for money or credentials. When you create synthetic media, disclose it clearly.",
    ],
    example: "A video of a CEO announcing a giveaway spreads on social media. The company's official site and press channel say nothing about it — it's a scam using a cloned likeness.",
    interactive: "real-or-ai",
    check: mc("single", "What's the most reliable way to judge a suspicious video?", ["Look closely for visual glitches", "*Verify the claim through the official source or independent outlets", "Trust it if it looks high-quality"], "Modern fakes often have no visible glitches. Source verification is far more reliable than appearance."),
    takeaway: "You can't spot every fake by eye — verify the source, and disclose synthetic media you create.",
    tool: checklist,
    resource: guide,
    meta: { ...nist, freshness: "fast-changing", nextReviewAt: "2027-01-12" },
  },
  {
    slug: "agent-permissions",
    title: "AI Agent Permissions and Least Privilege",
    minutes: 6,
    difficulty: "Intermediate",
    learn: ["Give agents only the permissions they actually need", "Why connected tools and external instructions raise risk"],
    explanation: [
      "An AI agent that can read your email, browse the web or send messages is only as safe as the permissions you grant it. Least privilege means: only the access the task requires, nothing more.",
      "Every connected tool increases risk, because untrusted content the agent reads can try to steer its actions (prompt injection). Important actions — sending, deleting, paying — should always need your approval, and you should review periodically what an agent can read, write, send or delete.",
    ],
    example: "A calendar-scheduling agent gets read access to your calendar and the ability to propose events — but sending invitations to external people requires your tap first.",
    interactive: "agent-permissions",
    check: mc("multi", "Which are good least-privilege habits for AI agents? (Choose all that apply)", ["*Grant only the permissions the task needs", "*Require approval for sending, deleting or paying", "Give full access so the agent never asks", "*Review connected tools regularly"], "Broad standing access turns one injected instruction into real damage. Narrow permissions and approvals contain it."),
    takeaway: "Agents get the minimum access needed, and people approve the actions that matter.",
    tool: checklist,
    resource: guide,
    meta: nist,
  },
]);
